<script lang="ts">
  import type { Finding, PreviewBlock, PreviewPara, Severity } from '@gostword/engine';

  let {
    blocks,
    byPara,
    selectedPara,
    onSelect,
  }: {
    blocks: PreviewBlock[];
    byPara: Map<number, Finding[]>;
    selectedPara: number | null;
    onSelect: (i: number) => void;
  } = $props();

  const OBJ = String.fromCharCode(0xfffc);
  const TW_PER_MM = 56.6929;
  const RANK: Record<Severity, number> = { error: 3, warning: 2, info: 1 };
  const ROLE_TAG: Record<string, string> = {
    heading: 'заголовок',
    caption: 'подпись',
    toc: 'содерж.',
    bib: 'источник',
    formula: 'формула',
    front: 'титул',
    'appendix-title': 'прилож.',
    code: 'код',
  };

  /** Для титула, содержания, списка источников и листингов метку ставим только у первого абзаца. */
  const REGION_ROLES = new Set(['front', 'toc', 'bib', 'code']);
  const tagged = $derived.by(() => {
    const set = new Set<number>();
    let prev: string | undefined;
    for (const b of blocks) {
      if (b.kind !== 'p') {
        prev = undefined;
        continue;
      }
      if (ROLE_TAG[b.role] && (!REGION_ROLES.has(b.role) || prev !== b.role)) set.add(b.i);
      if (b.role !== 'empty') prev = b.role;
    }
    return set;
  });

  interface Piece {
    kind: 'text' | 'obj' | 'tab' | 'br';
    text: string;
    flags: number;
    sev?: Severity;
  }

  function worst(list: Finding[] | undefined): Severity | undefined {
    let best: Severity | undefined;
    for (const f of list ?? []) if (!best || RANK[f.severity] > RANK[best]) best = f.severity;
    return best;
  }

  /** Режет текст абзаца на куски по границам начертаний и подсветок замечаний. */
  function pieces(p: PreviewPara, list: Finding[] | undefined): Piece[] {
    const n = p.text.length;
    const cuts = new Set<number>([0, n]);
    const marks: Array<[number, number, Severity]> = [];
    for (const f of list ?? []) {
      for (const [s, e] of f.marks ?? []) {
        if (s < 0 || e > n || e <= s) continue;
        marks.push([s, e, f.severity]);
        cuts.add(s);
        cuts.add(e);
      }
    }
    for (const [s, e] of p.spans ?? []) {
      cuts.add(s);
      cuts.add(e);
    }
    const points = [...cuts].sort((a, b) => a - b);
    const out: Piece[] = [];
    for (let k = 0; k + 1 < points.length; k++) {
      const a = points[k]!;
      const b = points[k + 1]!;
      if (a === b) continue;
      let flags = 0;
      for (const [s, e, fl] of p.spans ?? []) if (s <= a && b <= e) flags |= fl;
      let sev: Severity | undefined;
      for (const [s, e, sv] of marks) if (s <= a && b <= e && (!sev || RANK[sv] > RANK[sev])) sev = sv;
      let buf = '';
      const flush = (): void => {
        if (buf) out.push({ kind: 'text', text: buf, flags, sev });
        buf = '';
      };
      for (const ch of p.text.slice(a, b)) {
        if (ch === OBJ || ch === '\t' || ch === '\n') {
          flush();
          out.push({ kind: ch === OBJ ? 'obj' : ch === '\t' ? 'tab' : 'br', text: '', flags, sev });
        } else buf += ch;
      }
      flush();
    }
    return out;
  }

  function style(p: PreviewPara): string {
    const mm = (tw: number): string => (tw / TW_PER_MM).toFixed(2);
    return (
      `--pt:${p.size};--ind:${mm(p.indent)};--left:${mm(p.left)};text-align:${p.align};` +
      (p.bold ? 'font-weight:700;' : '') +
      (p.caps ? 'text-transform:uppercase;' : '')
    );
  }
</script>

<div class="paper">
  {#each blocks as b, bi (bi)}
    {#if b.kind === 'p'}
      {@render para(b, false)}
    {:else}
      <div class="table-wrap">
        <table class:plain={!b.bordered}>
          <tbody>
            {#each b.rows as row, ri (ri)}
              <tr>
                {#each row as cell, ci (ci)}
                  <td>
                    {#each cell as p (p.i)}{@render para(p, true)}{/each}
                  </td>
                {/each}
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {/if}
  {/each}
</div>

{#snippet para(p: PreviewPara, inTable: boolean)}
  {@const list = byPara.get(p.i)}
  {@const sev = worst(list)}
  {#if p.pageBreak && !inTable}
    <div class="page-break" aria-hidden="true"><span>новая страница</span></div>
  {/if}
  {#if list}
    <div
      class="para role-{p.role} has-issue"
      class:selected={selectedPara === p.i}
      data-sev={sev}
      data-i={p.i}
      style={style(p)}
      role="button"
      tabindex="0"
      onclick={() => onSelect(p.i)}
      onkeydown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(p.i);
        }
      }}
    >
      {@render content(p, list, inTable)}
      <span class="count" data-sev={sev}>{list.length}</span>
    </div>
  {:else}
    <div class="para role-{p.role}" data-i={p.i} style={style(p)}>
      {@render content(p, undefined, inTable)}
    </div>
  {/if}
{/snippet}

{#snippet content(p: PreviewPara, list: Finding[] | undefined, inTable: boolean)}
  {#if tagged.has(p.i) && !inTable}<span class="role-tag">{ROLE_TAG[p.role]}</span>{/if}
  {#if p.label}<span class="list-label">{p.label}</span>{/if}
  {#each pieces(p, list) as piece, k (k)}
    {#if piece.kind === 'obj'}
      <span class="obj" class:mark={!!piece.sev} data-sev={piece.sev}>{p.role === 'formula' ? 'формула' : 'рисунок'}</span>
    {:else if piece.kind === 'tab'}
      <span class="tab"></span>
    {:else if piece.kind === 'br'}
      <br />
    {:else}
      <span
        class:b={!!(piece.flags & 1)}
        class:i={!!(piece.flags & 2)}
        class:u={!!(piece.flags & 4)}
        class:sup={!!(piece.flags & 8)}
        class:sub={!!(piece.flags & 16)}
        class:mark={!!piece.sev}
        data-sev={piece.sev}>{piece.text}</span
      >
    {/if}
  {/each}
  {#if !p.text && !p.label}<span class="empty">¶</span>{/if}
{/snippet}

<style>
  .paper {
    --mm: 3.3px;
    position: relative;
    max-width: calc(210 * var(--mm));
    margin: 0 auto;
    padding: calc(20 * var(--mm)) calc(15 * var(--mm)) calc(20 * var(--mm)) calc(30 * var(--mm));
    border-radius: 4px;
    background: var(--paper);
    color: var(--paper-text);
    box-shadow: var(--shadow);
    font-family: var(--font-doc);
    line-height: 1.45;
  }

  .para {
    position: relative;
    min-height: 1.45em;
    margin-left: calc(var(--left) * var(--mm));
    font-size: calc(var(--pt) * 0.3528 * var(--mm));
    text-indent: calc(var(--ind) * var(--mm));
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    border-radius: 3px;
  }

  .para[data-sev='error'] {
    --c: #d23a2f;
    --c-soft: #fde8e6;
  }

  .para[data-sev='warning'] {
    --c: #c47a0c;
    --c-soft: #fdf0db;
  }

  .para[data-sev='info'] {
    --c: #566e95;
    --c-soft: #e8eef7;
  }

  .has-issue {
    cursor: pointer;
    transition: background 0.15s;
  }

  .has-issue::before {
    content: '';
    position: absolute;
    top: 0.2em;
    bottom: 0.2em;
    left: calc(-7 * var(--mm));
    width: 3px;
    border-radius: 2px;
    background: var(--c);
  }

  .has-issue:hover {
    background: color-mix(in srgb, var(--c-soft) 70%, transparent);
  }

  .has-issue.selected {
    background: var(--c-soft);
  }

  .has-issue:global(.flash) {
    animation: flash 1.4s ease-out;
  }

  @keyframes flash {
    0%,
    30% {
      background: var(--c-soft);
      box-shadow: 0 0 0 5px var(--c-soft);
    }
    100% {
      box-shadow: 0 0 0 5px transparent;
    }
  }

  .count {
    position: absolute;
    top: 0.15em;
    right: calc(-12 * var(--mm));
    min-width: 19px;
    height: 19px;
    padding: 0 5px;
    display: grid;
    place-items: center;
    border-radius: 999px;
    background: var(--c);
    color: #fff;
    font-family: var(--font-ui);
    font-size: 11px;
    font-weight: 700;
    line-height: 1;
    text-indent: 0;
    font-style: normal;
    text-transform: none;
  }

  .role-tag {
    position: absolute;
    top: 0.45em;
    left: calc(-30 * var(--mm));
    width: calc(22 * var(--mm));
    white-space: nowrap;
    color: #a29e93;
    font-family: var(--font-ui);
    font-size: 9px;
    font-weight: 560;
    letter-spacing: 0.05em;
    line-height: 1.1;
    text-align: right;
    text-indent: 0;
    text-transform: uppercase;
    font-style: normal;
  }

  .mark {
    border-radius: 2px;
    background: color-mix(in srgb, var(--mc) 20%, transparent);
    box-shadow: inset 0 -2px 0 var(--mc);
  }

  .mark[data-sev='error'] {
    --mc: #d23a2f;
  }

  .mark[data-sev='warning'] {
    --mc: #d98a17;
  }

  .mark[data-sev='info'] {
    --mc: #566e95;
  }

  .b {
    font-weight: 700;
  }

  .i {
    font-style: italic;
  }

  .u {
    text-decoration: underline;
  }

  .sup,
  .sub {
    font-size: 0.7em;
    vertical-align: super;
  }

  .sub {
    vertical-align: sub;
  }

  .tab {
    display: inline-block;
    width: 2.5em;
  }

  .obj {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 10em;
    min-height: 3.2em;
    margin: 0.3em 0;
    padding: 0 1.2em;
    border: 1px solid #dcd9cf;
    border-radius: 4px;
    background: repeating-linear-gradient(45deg, #f3f2ed, #f3f2ed 6px, #ebe9e2 6px, #ebe9e2 12px);
    color: #77746b;
    font-family: var(--font-ui);
    font-size: 11px;
    letter-spacing: 0.04em;
    text-indent: 0;
    text-transform: uppercase;
    vertical-align: middle;
  }

  .role-figure .obj {
    width: 62%;
    min-height: 8em;
  }

  .role-code {
    font-family: ui-monospace, 'Cascadia Mono', Consolas, 'Liberation Mono', monospace;
    font-size: calc(var(--pt) * 0.3528 * var(--mm) * 0.86);
    line-height: 1.35;
    text-align: left;
    background: #f6f5f0;
  }

  .list-label {
    display: inline-block;
    min-width: 1.5em;
    margin-right: 0.4em;
    text-indent: 0;
  }

  .empty {
    color: #d3d0c6;
  }

  .page-break {
    position: relative;
    height: 0;
    margin: calc(9 * var(--mm)) calc(-15 * var(--mm)) calc(9 * var(--mm)) calc(-30 * var(--mm));
    border-top: 1px dashed #d2cfc4;
  }

  .page-break span {
    position: absolute;
    left: 50%;
    padding: 0 10px;
    transform: translate(-50%, -50%);
    background: var(--paper);
    color: #a8a498;
    font-family: var(--font-ui);
    font-size: 10px;
    font-weight: 560;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  .table-wrap {
    margin: 0.3em 0 0.6em;
    overflow-x: auto;
  }

  table {
    width: 100%;
    border-collapse: collapse;
  }

  td {
    padding: 0.15em 0.4em;
    border: 1px solid #b9b5aa;
    vertical-align: top;
  }

  .plain td {
    border-color: transparent;
  }

  td .para {
    font-size: calc(var(--pt) * 0.3528 * var(--mm) * 0.92);
  }

  @media (max-width: 1180px) {
    .paper {
      --mm: 2.9px;
    }
  }

  @media (max-width: 900px) {
    .paper {
      --mm: calc((100vw - 32px) / 214);
      padding-left: calc(24 * var(--mm));
    }

    .role-tag {
      display: none;
    }

    .count {
      right: calc(-9 * var(--mm));
      min-width: 17px;
      height: 17px;
      font-size: 10px;
    }
  }
</style>
